import {
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  parcelsField,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G3. Zgłoszenie zmian danych objętych ewidencją gruntów i budynków (wniosek o aktualizację EGiB) — art. 22 ust. 2–3
 * i art. 24 ust. 2a–2b ustawy — Prawo geodezyjne i kartograficzne; § 30, 34 i 35 rozporządzenia MRPiT z 27.07.2021 r.
 * w sprawie EGiB. Brak wzoru ogólnopolskiego; układ jak wzory Starostwa Powiatowego w Krakowie
 * (https://geodezja.powiat.krakow.pl/wp-content/uploads/fgkik1.pdf) i Powiatu Siedleckiego
 * (https://samorzad.gov.pl/attachment/deb1cd8a-9bc9-4f8b-94c7-e910d0de26e9) — oba pobrane 22.10.2026: zgłaszający,
 * położenie nieruchomości, status (właściciel / współwłaściciel / władający), zgłaszane zmiany, załączniki. Lista typów
 * zmiany i przypisanie dokumentów źródłowych według katalogu (praktyka urzędów — do potwierdzenia z wydziałem).
 */
const has = (value: string): EbiCondition => ({ field: "rodzajZmiany", includes: value });
const SURVEY_REQUIRED: EbiCondition = { any: [has("budynek"), has("granice")] };
const SURVEY_SUGGESTED: EbiCondition = { any: [has("budynek"), has("granice"), has("uzytki")] };
const PREMISES: EbiCondition = has("lokal");

const G3: IEbiFormDefinition = {
  code: "G3",
  version: 1,
  title: "Zgłoszenie zmian danych objętych ewidencją gruntów i budynków",
  category: EEbiFormCategory.Geodesy,
  office: EEbiFormOffice.County,
  documentType: "Pismo",
  description: "Zgłoś zmianę właściciela lub władającego, użytków, budynku, lokalu albo adresu, aby urząd ujawnił ją w ewidencji gruntów i budynków.",
  keywords: ["ewidencja gruntów", "egib", "zmiana danych", "aktualizacja", "właściciel", "użytki", "budynek", "geodezja"],
  organ: "Starosta (prezydent miasta na prawach powiatu)",
  legalBasis:
    "art. 22 ust. 2 i art. 24 ust. 2a–2b ustawy z dnia 17 maja 1989 r. — Prawo geodezyjne i kartograficzne; § 30 i § 34 rozporządzenia Ministra Rozwoju, Pracy i Technologii z dnia 27 lipca 2021 r. w sprawie ewidencji gruntów i budynków",
  request:
    "Jako właściciel, współwłaściciel albo władający nieruchomością wskazaną poniżej zgłaszam zmiany danych ewidencyjnych opisane w zgłoszeniu, celem ich ujawnienia w operacie ewidencji gruntów i budynków.",
  feeLabel: "bez opłaty",
  fee: "Zmiana wprowadzana czynnością materialno-techniczną — bez opłaty; gdy wymaga wydania decyzji administracyjnej — opłata skarbowa 10 zł. Pełnomocnictwo — 17 zł.",
  deadlineLabel: "30 dni od zmiany",
  deadline: "Właściciel lub władający zgłasza zmiany w terminie 30 dni od dnia ich powstania (art. 22 ust. 2 ustawy).",
  sections: [
    applicantSection("Zgłaszający zmianę"),
    representationSection(),
    {
      code: "nieruchomosc",
      title: "Położenie nieruchomości, w której nastąpiły zmiany",
      fields: [
        { code: "gmina", type: "text", label: "Gmina", required: true, width: "half", maxLength: 100 },
        { code: "miejscowoscNieruchomosci", type: "text", label: "Miejscowość", width: "half", maxLength: 100 },
        parcelsField({ withLandRegister: true }),
        {
          code: "budynekLokal",
          type: "text",
          label: "Budynek lub lokal, którego dotyczy zmiana (adres, numer)",
          hint: "Gdy zmiana dotyczy budynku albo lokalu.",
          maxLength: 300,
        },
        {
          code: "status",
          type: "radio",
          label: "Zgłaszam zmiany jako",
          required: true,
          options: [
            { value: "wlasciciel", label: "właściciel" },
            { value: "wspolwlasciciel", label: "współwłaściciel" },
            { value: "wladajacy", label: "władający (np. użytkownik wieczysty, dzierżawca, zarządca)" },
          ],
        },
      ],
    },
    {
      code: "zmiany",
      title: "Zgłaszane zmiany danych ewidencyjnych",
      fields: [
        {
          code: "rodzajZmiany",
          type: "checkboxes",
          label: "Rodzaj zmiany",
          required: true,
          options: [
            { value: "wlasnosc", label: "zmiana właściciela lub władającego (np. sprzedaż, darowizna, spadek, dzierżawa)" },
            { value: "danePodmiotu", label: "zmiana danych właściciela lub władającego (np. nazwisko, adres)" },
            { value: "uzytki", label: "zmiana użytków gruntowych (sposobu użytkowania gruntu)" },
            { value: "budynek", label: "budynek — nowy, rozebrany albo zmiana jego danych" },
            { value: "lokal", label: "lokal — wyodrębnienie albo zmiana jego danych" },
            { value: "granice", label: "granice lub powierzchnia działek (np. podział, scalenie)" },
            { value: "adres", label: "adres nieruchomości (numer porządkowy)" },
            { value: "inne", label: "inna zmiana" },
          ],
        },
        { code: "opisZmian", type: "textarea", label: "Zgłaszam następujące zmiany danych ewidencyjnych", required: true, maxLength: 5000 },
        {
          code: "infoDokumenty",
          type: "info",
          label:
            "Dołącz dokument, z którego wynika zmiana: akt notarialny, prawomocne orzeczenie sądu, ostateczną decyzję administracyjną (np. o podziale, scaleniu, pozwoleniu na użytkowanie), zaświadczenie o samodzielności lokalu albo umowę dzierżawy. Przy zmianach budynków, granic i użytków starosta może wymagać dokumentacji geodezyjnej przyjętej do zasobu (art. 22 ust. 3 ustawy). Urząd może zażądać oryginałów dokumentów do wglądu.",
        },
        feeInfo(
          "infoOplata",
          "Zmiana wprowadzana czynnością materialno-techniczną jest bezpłatna. Jeżeli zmiana wymaga wydania decyzji administracyjnej (art. 24 ust. 2b ustawy), opłata skarbowa wynosi 10 zł — urząd poinformuje o tym.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "dokumentZrodlowy",
      label: "Dokument stanowiący podstawę zmiany",
      hint: "Akt notarialny, prawomocne orzeczenie sądu, ostateczna decyzja administracyjna, umowa — skan lub odpis.",
      multiple: true,
      required: true,
    },
    {
      code: "dokumentacjaGeodezyjna",
      label: "Dokumentacja geodezyjna — operat techniczny przyjęty do zasobu wraz z wykazem zmian danych ewidencyjnych",
      hint: "Przy zmianach budynków i granic; przy zmianie użytków — jeśli została sporządzona (art. 22 ust. 3 ustawy, § 34 rozporządzenia).",
      visibleWhen: SURVEY_SUGGESTED,
      requiredWhen: SURVEY_REQUIRED,
    },
    {
      code: "zaswiadczenieLokal",
      label: "Zaświadczenie o samodzielności lokalu",
      visibleWhen: PREMISES,
      required: true,
    },
    feeProofAttachment("Dowód zapłaty opłaty skarbowej (10 zł) — gdy zmiana wymaga decyzji", {
      required: false,
      hint: "Nieobowiązkowo — urząd poinformuje, jeśli opłata będzie należna.",
    }),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default G3;
